// Tiny exact-decimal helper.
//
// Money / price / lot / percent values arrive as decimal STRINGS and must never be turned
// into floats before summing (float drift would corrupt fleet totals). This module does
// exact fixed-point arithmetic with BigInt at a fixed scale, and only converts to `number`
// at the very end for display/coloring/sorting — where IEEE-754 precision is adequate.

const SCALE = 8;
const SCALE_FACTOR = 10n ** BigInt(SCALE);

const DECIMAL_RE = /^-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?$/;

/** True when `s` matches the wire decimal grammar. */
export function isDecimal(s: string): boolean {
  return DECIMAL_RE.test(s);
}

/** Parse a decimal string to a scaled BigInt. Throws on malformed input. */
function toScaled(s: string): bigint {
  if (!DECIMAL_RE.test(s)) {
    throw new Error(`invalid decimal: ${JSON.stringify(s)}`);
  }
  const neg = s.charCodeAt(0) === 45; // '-'
  const body = neg ? s.slice(1) : s;
  const dot = body.indexOf('.');
  let intPart: string;
  let fracPart: string;
  if (dot === -1) {
    intPart = body;
    fracPart = '';
  } else {
    intPart = body.slice(0, dot);
    fracPart = body.slice(dot + 1);
  }
  // Pad/truncate the fractional part to SCALE digits (extra precision beyond SCALE is
  // dropped; wire values never exceed it in practice).
  const frac = (fracPart + '0'.repeat(SCALE)).slice(0, SCALE);
  const scaled = BigInt(intPart || '0') * SCALE_FACTOR + BigInt(frac || '0');
  return neg ? -scaled : scaled;
}

/** Render a scaled BigInt back to a canonical trimmed decimal string. */
function fromScaled(v: bigint): string {
  const neg = v < 0n;
  const abs = neg ? -v : v;
  const intPart = abs / SCALE_FACTOR;
  let frac = (abs % SCALE_FACTOR).toString().padStart(SCALE, '0').replace(/0+$/, '');
  return (neg ? '-' : '') + intPart.toString() + (frac ? '.' + frac : '');
}

/** Exact sum of decimal strings. Non-decimal / null entries are skipped. */
export function sumDec(list: Array<string | null | undefined>): string {
  let acc = 0n;
  for (const s of list) {
    if (s == null || !DECIMAL_RE.test(s)) continue;
    acc += toScaled(s);
  }
  return fromScaled(acc);
}

/** Exact a + b. */
export function addDec(a: string, b: string): string {
  return fromScaled(toScaled(a) + toScaled(b));
}

/** Exact a - b. */
export function subDec(a: string, b: string): string {
  return fromScaled(toScaled(a) - toScaled(b));
}

/** Sign of a decimal string: -1, 0, or 1. */
export function signDec(s: string): -1 | 0 | 1 {
  const v = toScaled(s);
  return v < 0n ? -1 : v > 0n ? 1 : 0;
}

/**
 * Convert a decimal string to a `number` for display/coloring/sorting ONLY.
 * Returns `null` for null/undefined/blank input so callers can render "Unavailable".
 */
export function toNum(s: string | null | undefined): number | null {
  if (s == null || s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
